import React from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Local development only. `VITE_PREVIEW` is set by scripts/run.sh and is NEVER
// set by a production build, so this collapses to `false` and Vite drops the
// branch entirely — it is not shipped code that happens to be off.
//
// The gate is skipped, NOT the authentication: the dev server attaches a real
// RS256 token to every /graphql call and the backend verifies it in full. So a
// preview session is genuinely signed in, and an operation that would refuse a
// real caller refuses this one identically. Skipping the gate without that
// would produce a UI that renders and an API that refuses everything, which
// looks like a broken backend.
const PREVIEW = import.meta.env.VITE_PREVIEW === '1';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useKindeAuth();

  if (PREVIEW) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;